import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import { financeService } from '@/services/finance/finance.service';
import { useNotification } from '@/contexts/NotificationContext';
import { PAYMENT_SYSTEM_IN_SETTINGS_FORM_DEFAULTS } from '@/types/finance/payment-system-in-settings';

function parsePositiveIntegerOrders(value: string): { ok: true; orders: number } | { ok: false; message: string } {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { ok: false, message: 'Укажите максимум активных IN-заявок' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    return { ok: false, message: 'Введите целое число не менее 1' };
  }
  return { ok: true, orders: n };
}

function balanceToFormValue(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

function parseMinEffectiveBalance(value: string): { ok: true; balance: string | null } | { ok: false; message: string } {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { ok: true, balance: null };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, message: 'Введите неотрицательное число или оставьте поле пустым' };
  }
  return { ok: true, balance: trimmed };
}

export type PaymentSystemInSettingsDialogMode = 'create' | 'edit';

interface PaymentSystemInSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentSystemId: string | null;
  /** create — POST после создания IN-ПС; edit — GET, затем PUT или POST если записи ещё нет */
  mode: PaymentSystemInSettingsDialogMode;
  canSave: boolean;
  onSuccess?: () => void;
}

const PaymentSystemInSettingsDialog: React.FC<PaymentSystemInSettingsDialogProps> = ({
  open,
  onClose,
  paymentSystemId,
  mode,
  canSave,
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [maxActiveInOrders, setMaxActiveInOrders] = useState(
    String(PAYMENT_SYSTEM_IN_SETTINGS_FORM_DEFAULTS.max_active_in_orders),
  );
  const [minEffectiveBalance, setMinEffectiveBalance] = useState('');
  const [existingSettingsId, setExistingSettingsId] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !paymentSystemId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (mode === 'create') {
        setMaxActiveInOrders(String(PAYMENT_SYSTEM_IN_SETTINGS_FORM_DEFAULTS.max_active_in_orders));
        setMinEffectiveBalance('');
        setExistingSettingsId(undefined);
        setSubmitting(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setSubmitting(false);
      try {
        const data = await financeService.getPaymentSystemInSettings(paymentSystemId);
        if (cancelled) return;
        setMaxActiveInOrders(String(data.max_active_in_orders));
        setMinEffectiveBalance(balanceToFormValue(data.min_effective_balance));
        setExistingSettingsId(data.id);
      } catch (err: any) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        const msg =
          (Array.isArray(detail) && detail[0]?.msg) ||
          (typeof detail === 'string' ? detail : null) ||
          err.message ||
          'Не удалось загрузить настройки';
        showNotification(typeof msg === 'string' ? msg : 'Не удалось загрузить настройки', 'error');
        onClose();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, paymentSystemId, mode, onClose, showNotification]);

  const handleSubmit = async () => {
    if (!paymentSystemId || !canSave) return;

    const orders = parsePositiveIntegerOrders(maxActiveInOrders);
    if (!orders.ok) {
      showNotification(orders.message, 'error');
      return;
    }
    const balance = parseMinEffectiveBalance(minEffectiveBalance);
    if (!balance.ok) {
      showNotification(balance.message, 'error');
      return;
    }

    const payload = {
      max_active_in_orders: orders.orders,
      min_effective_balance: balance.balance,
    };

    setSubmitting(true);
    try {
      const shouldUpdate = mode === 'edit' && Boolean(existingSettingsId);
      if (shouldUpdate) {
        await financeService.updatePaymentSystemInSettings(paymentSystemId, payload);
        showNotification('Настройки IN обновлены', 'success');
      } else {
        await financeService.createPaymentSystemInSettings(paymentSystemId, payload);
        showNotification('Настройки IN сохранены', 'success');
      }
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg =
        (Array.isArray(detail) && detail[0]?.msg) ||
        (typeof detail === 'string' ? detail : null) ||
        err.message ||
        'Ошибка при сохранении настроек';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка при сохранении настроек', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'create' ? 'Настройки IN' : 'Редактировать настройки IN';

  return (
    <Dialog open={open} onClose={submitting || loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label="Макс. активных IN-заявок"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={maxActiveInOrders}
              onChange={(e) => setMaxActiveInOrders(e.target.value)}
              helperText="Целое число ≥ 1"
              disabled={submitting}
            />
            <TextField
              fullWidth
              label="Мин. эффективный баланс"
              value={minEffectiveBalance}
              onChange={(e) => setMinEffectiveBalance(e.target.value)}
              helperText="Пусто — без ограничения; иначе число или decimal-строка, как в API"
              disabled={submitting}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || loading}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || !paymentSystemId || !canSave}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentSystemInSettingsDialog;
